import AgoraRTM, {
  RtmChannel,
  RtmClient,
  RtmMessage,
  RtmStatusCode,
} from 'agora-rtm-sdk';
import { EventEmitter } from 'events';
import { Debug } from '../../common/utils/debugLogs';
import { liveEndpoints } from '../api/apiProvider';
import { store } from '../../store/store';
import { AGORA_APP_ID } from '../../loadEnv';
import { EXPIRED_TOKEN_TIME } from '../../common/constants/globalConstants';

export class RtmEngine extends EventEmitter {
  instance: RtmClient;
  channel?: RtmChannel; // Map<channelID, channel instance>
  isLoggedIn = false;

  constructor() {
    super();
    this.instance = AgoraRTM.createInstance(AGORA_APP_ID);
    this.subscribeClientEvents();
  }

  // login RTM SDK with userId
  public async login(
    uid: string,
    channelId: string,
    liveId?: string,
  ): Promise<void> {
    const getLiveTokenRTM = liveEndpoints.getLiveTokenRTM.initiate;
    let token: string | undefined = undefined;
    if (!liveId) {
      liveId = channelId;
    }
    try {
      const res = await store
        .dispatch(
          getLiveTokenRTM({
            channelId,
            expire: EXPIRED_TOKEN_TIME,
            uid,
            liveId,
          }),
        )
        .unwrap();
      token = res.token;
    } catch (error) {
      console.error('getLiveTokenRTM error: ', error);
    }

    return this.instance
      .login({
        uid,
        token: token || undefined,
      })
      .then(() => {
        if (token) {
          this.instance.removeAllListeners('TokenPrivilegeWillExpire');
          this.instance.on('TokenPrivilegeWillExpire', () => {
            this.refetchToken(uid, channelId, liveId!);
          });
        }
      });
  }

  async refetchToken(uid: string, channelId: string, liveId: string) {
    const getLiveTokenRTM = liveEndpoints.getLiveTokenRTM.initiate;
    let token;
    try {
      const res = await store
        .dispatch(
          getLiveTokenRTM({
            channelId,
            expire: EXPIRED_TOKEN_TIME,
            uid,
            liveId,
          }),
        )
        .unwrap();
      token = res.token;
      this.instance.renewToken(token).then(() => {
        Debug.log(`RTM -> Renew Token successfully`);
      });
    } catch (error) {
      console.error('refetch token RTM error: ', error);
    }
  }

  // This is event from client
  private subscribeClientEvents(): void {
    // connection state changed
    this.instance.on(
      'ConnectionStateChanged',
      (
        newState: RtmStatusCode.ConnectionState,
        reason: RtmStatusCode.ConnectionChangeReason,
      ): void => {
        this.emit('ConnectionStateChanged', { newState, reason });
      },
    );

    // New message from other client
    this.instance.on(
      'MessageFromPeer',
      (message: RtmMessage, peerId: string): void => {
        this.emit('MessageFromPeer', { message, peerId });
      },
    );
  }

  // This is events from channel
  private subscribeChannelEvents(): void {
    if (this.channel === undefined) {
      return;
    }

    const channel = this.channel;
    // Emit event when new message was added to this channel
    channel.on(
      'ChannelMessage',
      (message: RtmMessage, memberId: string): void => {
        Debug.log(`RTM -> ${JSON.stringify(message)}`);
        this.emit('ChannelMessage', message, memberId);
      },
    );

    channel.on('MemberJoined', (memberId: string) => {
      this.emit('MemberJoined', memberId);
    });

    channel.on('MemberLeft', (memberId: string) => {
      this.emit('MemberLeft', memberId);
    });

    channel.on('MemberCountUpdated', (memberCount: number) => {
      this.emit('MemberLeft', memberCount);
    });
  }

  /**
   * Join RTM channel
   * @param channelId channel id to join
   */
  public joinChannel(channelId: string): void {
    if (channelId === this.channel?.channelId) {
      return;
    }

    this.channel = this.instance.createChannel(channelId);
    // join and subscribse channel event
    this.channel
      ?.join()
      .then(() => {
        console.log(`RTM -> join channel success`);
        this.subscribeChannelEvents(); // subscribse for channel events
      })
      .catch((error) => {
        console.log(`RTM -> join channel error: ${error}`);
      });
  }

  /**
   * destroy and cleanup
   */
  public destroy(): void {
    if (this.channel === undefined) {
      return;
    }
    this.instance.removeAllListeners();
    this.channel.removeAllListeners();
    this.channel.leave();
  }
}
